import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import { BodyLarge, BodySmall, Button, H1, H6 } from '@atlassian/mpac-primitives';
import { css } from '@compiled/react';
import React from 'react';

import type { BundleConfig } from '../config/bundleConfig';

const heroContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: token('space.150', '12px'),
  marginBottom: token('space.800'),
  marginTop: token('space.800'),
  [media.above.md]: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
});

const heroContentStyles = css({
  width: '100%',
  [media.above.md]: {
    width: 'auto',
    flex: '1',
  },
});

const desktopHeroImageStyles = css({
  display: 'none',
  [media.above.md]: {
    display: 'flex',
    justifyContent: 'center',
    width: '40%',
  },
});

const heroAppsContainerStyles = css({
  display: 'flex',
  width: '100%',
  marginTop: token('space.400'),
  marginBottom: token('space.100'),
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  gap: 0,
});

const heroAppCardStyles = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  width: '100%',
  marginBottom: token('space.500', '40px'),
  [media.above.sm]: {
    width: '48%',
  },
});

const heroAppIconContainerStyles = css({
  width: '3.125rem',
  height: '3.125rem',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  marginRight: token('space.150'),
  borderRadius: '8px',
  border: `1px solid ${token('color.border')}`,
  padding: '9px',
});

const heroAppContentStyles = css({
  display: 'flex',
  flexDirection: 'column',
});

const heroAppTitleStyles = css({
  margin: 0,
});

const heroAppDescriptionStyles = css({
  margin: 0,
});

const heroTitleStyles = css({
  fontWeight: '700',
  marginTop: 0,
});

const bundleHeaderImageStyles = css({
  marginBottom: token('space.300'),
});

interface HeroAppCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
}

interface HeroSectionProps {
  bundleConfig: BundleConfig;
  onRequestClick: () => void;
}

const HeroAppCard = ({ title, description, icon }: HeroAppCardProps) => {
  return (
    <div css={heroAppCardStyles}>
      <div css={heroAppIconContainerStyles}>{icon}</div>
      <div css={heroAppContentStyles}>
        <H6 css={heroAppTitleStyles}>{title}</H6>
        <BodySmall css={heroAppDescriptionStyles}>{description}</BodySmall>
      </div>
    </div>
  );
};

export const HeroSection: React.FC<HeroSectionProps> = ({ bundleConfig, onRequestClick }) => {
  return (
    <div
      css={[
        heroContainerStyles,
        {
          marginBottom: bundleConfig.apps.length > 2 ? token('space.800') : token('space.400'),
        },
      ]}
    >
      <div css={heroContentStyles}>
        <img
          src={bundleConfig.hero.headerImage}
          alt={bundleConfig.hero.headerImageAlt}
          css={bundleHeaderImageStyles}
        />
        <H1 css={heroTitleStyles}>{bundleConfig.hero.title}</H1>
        <BodyLarge>{bundleConfig.hero.description}</BodyLarge>
        <div css={heroAppsContainerStyles}>
          {bundleConfig.apps.map((app, index) => (
            <HeroAppCard
              key={index}
              icon={<img src={app.icon} alt={app.iconAlt} width="32px" height="32px" />}
              title={app.title}
              description={app.description}
            />
          ))}
        </div>
        {bundleConfig.hero.requestButton && (
          <Button appearance="primary" onClick={onRequestClick}>
            {bundleConfig.hero.requestButton}
          </Button>
        )}
      </div>
      <div css={desktopHeroImageStyles}>
        <img
          src={bundleConfig.hero.heroImage}
          alt={bundleConfig.hero.heroImageAlt}
          style={{ height: '480px' }}
        />
      </div>
    </div>
  );
};
